/**
 * Bug reports: the words, the rules for who moves one where, and the shape
 * of what the app was doing when it was reported. Decision 117.
 *
 * Anybody signed in reports a bug from Help, with a picture, a comment and
 * the trail the screen kept (clicks, screen changes, console messages and
 * requests). The person who reported it follows it to fixed; somebody with
 * `BUG_MANAGE` — Peter, or an AI session working as him over MCP — moves it
 * along and says how it was changed. This file is the only place those
 * rules are written, so the web, the router and the MCP tools agree.
 */

export const BUG_STATUSES = ['OPEN', 'IN_PROGRESS', 'NEEDS_INFO', 'FIXED', 'DONE', 'CLOSED'] as const
export type BugStatus = (typeof BUG_STATUSES)[number]

export const BUG_STATUS_LABEL: Record<BugStatus, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'Being fixed',
  NEEDS_INFO: 'Question asked',
  FIXED: 'Fixed — please check',
  DONE: 'Done',
  CLOSED: 'Closed',
}

/** One line on what each status means, for the person reading the list. */
export const BUG_STATUS_CAPTION: Record<BugStatus, string> = {
  OPEN: 'Reported and waiting to be looked at.',
  IN_PROGRESS: 'Somebody is working on it.',
  NEEDS_INFO: 'A question was asked; the answer is needed to go on.',
  FIXED: 'A fix is in the app. Check it and say whether it worked.',
  DONE: 'Fixed, and checked by the person who reported it.',
  CLOSED: 'Closed without a change, with the reason on the report.',
}

/** Finished with: the list hides these unless asked. */
export const BUG_FINISHED: readonly BugStatus[] = ['DONE', 'CLOSED']

/**
 * Whose move it is. The reporter's when there is a fix to check or a
 * question to answer; the fixer's while it is open or being fixed; nobody's
 * once it is done or closed.
 */
export function bugWaitingOn(status: BugStatus): 'reporter' | 'fixer' | null {
  if (status === 'FIXED' || status === 'NEEDS_INFO') return 'reporter'
  if (status === 'OPEN' || status === 'IN_PROGRESS') return 'fixer'
  return null
}

/**
 * A status change by somebody with `BUG_MANAGE` needs words when the reporter
 * will act on them: how it was changed (Fixed), what is being asked
 * (Question asked), or why nothing will change (Closed).
 */
export function bugNoteRequired(to: BugStatus): string | null {
  if (to === 'FIXED') return 'Say how it was changed, so they know what to check.'
  if (to === 'NEEDS_INFO') return 'Write the question.'
  if (to === 'CLOSED') return 'Say why it is being closed.'
  return null
}

/** What the reporter's own buttons do. A fixer can set any status. */
export type BugReporterAction = 'reply' | 'confirm' | 'reopen' | 'withdraw'

/**
 * The status a reporter's action leaves the report in, or `null` when the
 * action is not offered at that status. A reply to a question hands it back;
 * a reply at any other time is only a comment.
 */
export function reporterTransition(status: BugStatus, action: BugReporterAction): BugStatus | null {
  switch (action) {
    case 'reply':
      return status === 'NEEDS_INFO' ? 'OPEN' : status
    case 'confirm':
      return status === 'FIXED' ? 'DONE' : null
    case 'reopen':
      return status === 'FIXED' || status === 'DONE' ? 'OPEN' : null
    case 'withdraw':
      return BUG_FINISHED.includes(status) ? null : 'CLOSED'
  }
}

/** The list's title: the comment's first line, cut at a word. */
export function bugTitle(comment: string, max = 90): string {
  const line = comment.trim().split(/\r?\n/)[0]?.trim() ?? ''
  if (line.length <= max) return line || 'No comment'
  const cut = line.slice(0, max)
  const space = cut.lastIndexOf(' ')
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`
}

/* -------------------------------------------------------------------------- */
/* The trail                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * One thing the screen noticed before the report. `t` is epoch milliseconds.
 * Values typed into a box are never kept: a field is named, not read.
 */
export type BugTrailEvent =
  | { t: number; kind: 'click'; target: string; detail?: string; path: string }
  | { t: number; kind: 'input'; target: string; value?: string; path: string }
  | { t: number; kind: 'nav'; to: string }
  | { t: number; kind: 'console'; level: 'error' | 'warn' | 'info' | 'log'; message: string }
  | { t: number; kind: 'error'; message: string; stack?: string; source?: string }
  | {
      t: number
      kind: 'request'
      method: string
      url: string
      status: number | null
      ms: number
      error?: string
    }

export type BugTrailKind = BugTrailEvent['kind']

/** The device and copy of the app it came from. */
export interface BugContext {
  url: string
  title?: string
  userAgent?: string
  platform?: string
  language?: string
  timeZone?: string
  viewport?: { width: number; height: number }
  screen?: { width: number; height: number }
  pixelRatio?: number
  /** The field app (phone or touch-first), not the office layout. */
  field?: boolean
  /** Installed to the home screen. */
  standalone?: boolean
  online?: boolean
  connection?: string
  /** The bundle's build id; the server adds its own and the release. */
  clientBuild?: string
  /** Milliseconds since the page loaded. */
  uptimeMs?: number
  memoryMb?: number
  /** How the picture was taken: a dragged area, the whole screen, or none. */
  picture?: 'snip' | 'screen' | 'none'
}

/** Limits the server holds the report to, so a runaway console cannot fill a row. */
export const BUG_LIMITS = {
  comment: 4000,
  reply: 4000,
  trailEvents: 300,
  trailText: 2000,
  screenshotBytes: 8 * 1024 * 1024,
} as const

const SECRET_PATTERNS: Array<[RegExp, string]> = [
  // EcoPest's own MCP and personal tokens (decision 116).
  [/\beco[par]_[A-Za-z0-9_-]{8,}/g, 'eco…_[hidden]'],
  [/\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]{8,}/gi, '$1 [hidden]'],
  [/\b(password|passwd|pwd|secret|token|api[_-]?key|access[_-]?token|refresh[_-]?token|session)(["']?\s*[:=]\s*["']?)[^\s"'&,;}]+/gi, '$1$2[hidden]'],
  // A JWT, wherever it turns up.
  [/\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g, '[hidden token]'],
]

/** Strips anything that looks like a credential from text the screen kept. */
export function scrubSecrets(text: string): string {
  let out = text
  for (const [pattern, replacement] of SECRET_PATTERNS) out = out.replace(pattern, replacement)
  return out
}

/** Text from the trail, scrubbed and cut to a length a row can hold. */
export function trailText(text: string, max: number = BUG_LIMITS.trailText): string {
  const clean = scrubSecrets(text)
  return clean.length > max ? `${clean.slice(0, max)}… (${clean.length - max} more characters)` : clean
}

/** One line per event, for a plain-text report (MCP, copy as text). */
export function describeTrailEvent(event: BugTrailEvent): string {
  switch (event.kind) {
    case 'click':
      return `Clicked ${event.target}${event.detail ? ` (${event.detail})` : ''} on ${event.path}`
    case 'input':
      return `Changed ${event.target}${event.value ? ` to “${event.value}”` : ''} on ${event.path}`
    case 'nav':
      return `Went to ${event.to}`
    case 'console':
      return `Console ${event.level}: ${event.message}`
    case 'error':
      return `Error: ${event.message}${event.source ? ` (${event.source})` : ''}${event.stack ? `\n${event.stack}` : ''}`
    case 'request':
      return `${event.method} ${event.url} → ${event.status ?? 'no answer'} in ${event.ms} ms${event.error ? ` — ${event.error}` : ''}`
  }
}
